import { HStack } from "@chakra-ui/react";
import { RadioProps, useRadioGroup } from "@chakra-ui/react";
import { RadioCard } from "../../../../molecules/RadioCard/NumberRadioCard";
import { useEffect, useMemo, useState } from "react";
import { FormControlQuestionSearch } from "../../../../molecules/FormControl/FormControlQuestionSearch";
import { FaRegStar, FaStar } from "react-icons/fa";
import { FormatReplic } from "../../../../templates/FormatReplic";
import { FormErrorMessageDefault } from "../../../../atoms/FormErrorMessage/FormErrorMessageDefault";
import { nextStep } from "../../../../../utils/nextStep";
import { useMediaQuery } from "@chakra-ui/react";
import { FieldError, Merge, FieldErrorsImpl } from "react-hook-form";
import { ClearButton } from "../../../../atoms/Button/ClearButton";

interface IProps extends RadioProps {
  label: string;
  name: string;
  description?: string;
  has_promoter_replica?: boolean;
  has_neutral_replica?: boolean;
  has_detractor_replica?: boolean;
  promoter_replica?: string;
  neutral_replica?: string;
  detractor_replica?: string;
  setValue: (value: string, replica: string) => void;
  selectColor: string;
  defaultValue?: string;
  defaultReplica?: string;
  error?:
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | boolean
    | undefined;
  step?: number;
  setStep?: (value: number) => void;
  handleTrigger?: (value: string) => void;
  questionId?: string;
  updateError?: (value: string, error: boolean) => void;
}

export function RatingRadioQuestionDefault(props: IProps) {
  const [hasError, setHasError] = useState<boolean>();
  const [selected, setSelected] = useState<string>(props.defaultValue ?? "");
  const [replica, setReplica] = useState<string>(props.defaultReplica ?? "");
  const [focus, setFocus] = useState<boolean>(false);
  const options = ["1", "2", "3", "4", "5"];

  const sortingValue = useMemo(() => {
    if (Number(selected) === 5) {
      return "promoter";
    } else if (Number(selected) === 4 || Number(selected) === 3) {
      return "neutral";
    } else if (Number(selected) <= 2 && Number(selected) > 0) {
      return "detractor";
    }
    return "";
  }, [selected]);

  useEffect(() => {
    setHasError(!!props.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, props.error]);

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: props.name,
    value: selected,
    onChange: (newValue) => {
      setSelected(newValue);
      props.setValue(newValue, replica);
      if (props.updateError && props.questionId && props.error) {
        props.updateError(
          `quizResponses.${props.questionId}`,
          props.error as boolean
        );
      }
      if (props.step !== undefined && props.setStep !== undefined) {
        const checkNextStep = nextStep({
          checkValue: newValue,
          has_replica_detractor: props.has_detractor_replica,
          has_replica_neutral: props.has_neutral_replica,
          has_replica_promoter: props.has_promoter_replica,
          type: "Rating",
        });
        if (checkNextStep) {
          props.setStep(props.step! + 1);
          if (props.handleTrigger) {
            props.handleTrigger(`quizResponses.${props.questionId}`);
          }
        }
      }
    },
  });

  const group = getRootProps();

  const [isMobile] = useMediaQuery("(max-width: 370px)");

  return (
    <>
      <FormControlQuestionSearch
        isRequired={props.isRequired}
        label={props.label}
        name={props.name}
        description={props.description}
      >
        {hasError && <FormErrorMessageDefault error="Questão obrigatória" />}
        <HStack wrap="nowrap" justifyContent="center" {...group}>
          {options.map((option) => {
            const radio = getRadioProps({ value: option });

            return (
              <RadioCard
                h="50px"
                w={isMobile ? "30px" : "50px"}
                p="5px"
                border="none"
                boxShadow="none"
                bg="transparent"
                _checked={{}}
                {...radio}
              >
                {Number(option) <= Number(selected) ? (
                  <FaStar color="#E9B93E" size={isMobile ? 30 : 40} />
                ) : (
                  <FaRegStar color="#E9B93E" size={isMobile ? 30 : 40} />
                )}
              </RadioCard>
            );
          })}
        </HStack>
      </FormControlQuestionSearch>
      <FormatReplic
        detractor_replica={props.detractor_replica}
        neutral_replica={props.neutral_replica}
        promoter_replica={props.promoter_replica}
        sortingValue={sortingValue}
        value={selected}
        has_detractor_replica={props.has_detractor_replica}
        has_neutral_replica={props.has_neutral_replica}
        has_promoter_replica={props.has_promoter_replica}
        focus={focus}
        color={props.selectColor}
        setValue={(value: any, result: string) => props.setValue(value, result)}
        setReplica={(value: string) => setReplica(value)}
        setFocus={(value: boolean) => setFocus(value)}
        defaultReplica={replica}
      />
      {!props.isRequired && selected && (
        <ClearButton
          onClick={() => {
            setSelected("");
            setReplica("");
            setFocus(false);
            props.setValue("", "");
          }}
        />
      )}
    </>
  );
}
